import { lazy, Suspense } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ProtectedRoute from './lib/ProtectedRoute';
import AdminRoute from './lib/AdminRoute';
import { useAuth } from './lib/AuthContext';
import { isAdmin } from './lib/auth';
import { ImpersonationProvider, useIdentity } from './lib/ImpersonationContext';
import ImpersonationPicker from './lib/ImpersonationPicker';

// Lazy-load page components so the initial bundle only includes the login
// flow and nav shell. Each page (and its dependencies) is fetched on first
// navigation. Vite's code-splitting handles the chunk boundaries automatically.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Vtt = lazy(() => import('./pages/Vtt'));
const Sheet = lazy(() => import('./pages/Sheet'));
const Party = lazy(() => import('./pages/Party'));
const Lore = lazy(() => import('./pages/Lore'));
const LevelUp = lazy(() => import('./pages/LevelUp'));
const Admin = lazy(() => import('./pages/Admin'));
const AuditDashboard = lazy(() => import('./pages/AuditDashboard'));

function AppShell() {
  const { user, signOut } = useAuth();
  const { impersonating, effectiveUser, stopImpersonating } = useIdentity();
  const admin = isAdmin(user);
  // Suppress admin-only nav links while impersonating — the DM should see
  // the nav exactly as the player would. The ImpersonationPicker and the
  // banner's "Return to DM view" button remain visible for control.
  const showAdminNav = admin && !impersonating;

  return (
    <div className="app-shell">
      {impersonating && (
        <div className="impersonation-banner">
          <span>
            Viewing as <strong>{effectiveUser?.email || effectiveUser?.username}</strong>
          </span>
          <button onClick={stopImpersonating}>Return to DM view</button>
        </div>
      )}
      <nav className="app-nav">
        <span className="app-nav__brand">GoobDoob VTT</span>
        {user && (
          <>
            <NavLink to="/dashboard" className="app-nav__link">Dashboard</NavLink>
            <NavLink to="/vtt" className="app-nav__link">VTT</NavLink>
            <NavLink to="/sheet" className="app-nav__link">Sheet</NavLink>
            <NavLink to="/party" className="app-nav__link">Party</NavLink>
            <NavLink to="/lore" className="app-nav__link">Lore</NavLink>
            <NavLink to="/levelup" className="app-nav__link">Level Up</NavLink>
            {showAdminNav && <NavLink to="/audit" className="app-nav__link">Audit</NavLink>}
            {showAdminNav && <NavLink to="/admin" className="app-nav__link">Admin</NavLink>}
            {admin && <ImpersonationPicker />}
            <span style={{ marginLeft: 'auto', color: 'var(--fg-muted)' }}>
              {user.email || user.username}
            </span>
            <button
              onClick={signOut}
              className="app-nav__link"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Sign out
            </button>
          </>
        )}
      </nav>
      <main className="app-main">
        <Suspense fallback={<p style={{ padding: 'var(--sp-4)', color: 'var(--fg-muted)' }}>Loading…</p>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="/dashboard"
            element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
          />
          <Route
            path="/vtt"
            element={<ProtectedRoute><Vtt /></ProtectedRoute>}
          />
          <Route
            path="/sheet"
            element={<ProtectedRoute><Sheet /></ProtectedRoute>}
          />
          <Route
            path="/party"
            element={<ProtectedRoute><Party /></ProtectedRoute>}
          />
          <Route
            path="/lore"
            element={<ProtectedRoute><Lore /></ProtectedRoute>}
          />
          <Route
            path="/levelup"
            element={<ProtectedRoute><LevelUp /></ProtectedRoute>}
          />
          <Route
            path="/audit"
            element={<AdminRoute><AuditDashboard /></AdminRoute>}
          />
          <Route
            path="/admin"
            element={<AdminRoute><Admin /></AdminRoute>}
          />
        </Routes>
        </Suspense>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <ImpersonationProvider>
      <AppShell />
    </ImpersonationProvider>
  );
}
